import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BriefcaseIcon,
  DocumentTextIcon,
  ChartBarIcon,
  SparklesIcon,
  RocketLaunchIcon,
  ClipboardDocumentCheckIcon,
  LightBulbIcon,
  CheckCircleIcon,
  BellAlertIcon,
  MapIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';

const HomePage: React.FC = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const gridBg = `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 0V100M21 0V100M41 0V100M61 0V100M81 0V100M0 1H100M0 21H100M0 41H100M0 61H100M0 81H100' stroke='white' stroke-opacity='0.1'/%3E%3C/svg%3E")`;

  const features = [
    {
      icon: <RocketLaunchIcon className="w-8 h-8" />,
      title: "Extensive Job Database",
      description: "Access thousands of curated job listings from across the internet, all in one place. No more juggling multiple job boards."
    },
    {
      icon: <LightBulbIcon className="w-8 h-8" />,
      title: "Smart Job Recommendations",
      description: "Get personalized job matches based on your skills, experience, and preferences. Our AI learns from your interactions to deliver better results."
    },
    {
      icon: <DocumentTextIcon className="w-8 h-8" />,
      title: "AI-Powered CV Analysis",
      description: "Optimize your resume with intelligent feedback and tailored suggestions for each job application."
    },
    {
      icon: <ClipboardDocumentCheckIcon className="w-8 h-8" />,
      title: "Application Tracking",
      description: "Keep all your applications organized with our intuitive dashboard. Track progress, deadlines, and follow-ups effortlessly."
    },
    {
      icon: <MapIcon className="w-8 h-8" />,
      title: "Interview Roadmap",
      description: "Navigate the hiring process confidently with our structured interview preparation system and progress tracking."
    },
    {
      icon: <BellAlertIcon className="w-8 h-8" />,
      title: "Real-Time Alerts",
      description: "Stay ahead with instant notifications for new relevant jobs and application updates."
    }
  ];

  const benefits = [
    "Personalized job matching using advanced AI",
    "Time-saving automated application tracking",
    "Smart filtering to remove irrelevant listings",
    "Data-driven insights for better applications",
    "Comprehensive interview preparation tools",
    "Continuous learning from your preferences"
  ];

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      {/* <Header /> */}
      <div className="flex-1 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section - Modern Glassmorphism Design */}
        <div className="relative min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-purple-700/90 overflow-hidden">
            {/* Modern Abstract Shapes */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
              <div className="absolute w-96 h-96 -top-12 -left-12 bg-blue-500/30 rounded-full blur-3xl" />
              <div className="absolute w-96 h-96 top-1/2 right-0 bg-purple-500/30 rounded-full blur-3xl" />
              <div className="absolute w-96 h-96 bottom-0 left-1/4 bg-pink-500/20 rounded-full blur-3xl" />
            </div>
          </div>

          <div className="relative container mx-auto px-4 py-20">
            <motion.div 
              className="max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-center mb-12">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="inline-block mb-6"
                >
                  <div className="p-3 bg-white/10 backdrop-blur-xl rounded-2xl inline-block">
                    <SparklesIcon className="h-16 w-16 text-white" />
                  </div>
                </motion.div>
                
                <h1 className="text-6xl md:text-7xl font-bold mb-8 text-white">
                  Your Dream Job,{' '}
                  <span className="bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-transparent">
                    Simplified
                  </span>
                </h1>
                
                <h2 className="text-2xl md:text-3xl font-medium mb-8 text-blue-100 leading-relaxed">
                  Job Searching, Reimagined with AI
                </h2>
                
                <motion.p 
                  className="text-xl text-blue-100/90 leading-relaxed mb-12 max-w-2xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  Say goodbye to endless scrolling. Let AI find your perfect role while you focus on what matters most.
                </motion.p>
              </div>

              <motion.div 
                className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Link
                  to="/register"
                  className="group relative overflow-hidden px-8 py-4 bg-white rounded-2xl font-semibold text-blue-900 hover:shadow-xl transition-all duration-300 flex items-center space-x-2"
                >
                  <span>Get Started Free</span>
                  <ArrowRightIcon className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
                
                <button
                  onClick={() => setIsVideoOpen(true)}
                  className="group px-8 py-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl font-semibold text-white hover:bg-white/20 transition-all duration-300 flex items-center space-x-2"
                >
                  <span>Watch Demo</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <ArrowRightIcon className="w-5 h-5" />
                  </motion.div>
                </button>
              </motion.div>

              {/* Stats Section */}
              <motion.div 
                className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                {[
                  { number: '10K+', label: 'Active Users' },
                  { number: '98%', label: 'Success Rate' },
                  { number: '24/7', label: 'AI Support' }
                ].map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 text-center">
                    <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
                    <div className="text-blue-100">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Features Grid - Modern Cards */}
        <div className="py-32 container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Why Choose JOE AI?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of job hunting with our AI-powered platform
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                index={index}
              />
            ))}
          </div>
        </div>

        {/* Benefits Section - Modern List */}
        <div className="py-32 bg-gradient-to-b from-gray-50 to-blue-50/50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-center mb-20 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Land Your Dream Job Faster
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group flex items-center space-x-4 bg-white/80 backdrop-blur-xl p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <div className="p-2 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors duration-300">
                      <CheckCircleIcon className="w-6 h-6 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative bg-gradient-to-r from-blue-900 to-purple-900 text-white py-24">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: gridBg, backgroundSize: '40px 40px' }} />
          <div className="relative container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Job Search?</h2>
              <p className="text-xl mb-12 text-blue-100">
                Join thousands of professionals who are letting JOE AI handle their job search 
                while they focus on what matters most.
              </p>
              <motion.div 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <Link
                  to="/register"
                  className="px-12 py-5 bg-white text-blue-900 rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl inline-block"
                >
                  Start Your Free Trial Today
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300">
          <div className="container mx-auto px-4 py-12">
            {/* Main Footer Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-8">
              {/* Brand Section */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <BriefcaseIcon className="w-8 h-8 text-blue-400" />
                  <span className="text-2xl font-bold text-white">JOE</span>
                </div>
                <p className="text-gray-400">
                  Your AI-powered job search assistant that helps you land your dream job faster and smarter.
                </p>
                <div className="flex space-x-4">
                  <a href="https://twitter.com/soffoalbert" className="hover:text-blue-400 transition-colors">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="https://www.linkedin.com/company/bros-joe-ai/about/?viewAsMember=true" className="hover:text-blue-400 transition-colors">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </a>
                </div>
              </div>
              
              {/* Quick Links */}
              <div>
                <h3 className="text-white font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li><Link to="/jobs" className="hover:text-blue-400 transition-colors">Browse Jobs</Link></li>
                  <li><Link to="/jobs" className="hover:text-blue-400 transition-colors">CV Analysis</Link></li>
                  <li><Link to="/pricing" className="hover:text-blue-400 transition-colors">Pricing</Link></li>
                  <li><Link to="/about" className="hover:text-blue-400 transition-colors">About Us</Link></li>
                </ul>
              </div>
              
              {/* Resources */}
              <div>
                <h3 className="text-white font-semibold mb-4">Resources</h3>
                <ul className="space-y-2">
                  <li><Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link></li>
                  <li><Link to="/guides" className="hover:text-blue-400 transition-colors">Career Guides</Link></li>
                  <li><Link to="/faq" className="hover:text-blue-400 transition-colors">FAQ</Link></li>
                  <li><Link to="/support" className="hover:text-blue-400 transition-colors">Support</Link></li>
                </ul>
              </div>
              
              {/* Contact */}
              <div>
                <h3 className="text-white font-semibold mb-4">Contact Us</h3>
                <div className="space-y-3">
                  <a href="mailto:soffoalbert@gmail.com" className="flex items-center space-x-2 hover:text-blue-400 transition-colors">
                    <EnvelopeIcon className="w-5 h-5" />
                    <span>soffoalbert@gmail.com</span>
                  </a>
                  <p className="text-sm text-gray-400">
                    Subscribe to our newsletter for the latest job search tips and updates.
                  </p>
                  <form className="flex space-x-2">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="flex-1 px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:border-blue-500"
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Subscribe
                    </button>
                  </form>
                </div>
              </div>
            </div>
            
            {/* Bottom Bar */}
            <div className="pt-8 mt-8 border-t border-gray-800 text-sm">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <div className="text-gray-400">
                  © {new Date().getFullYear()} JOE AI. All rights reserved.
                </div>
                <div className="flex space-x-6">
                  <Link to="/privacy" className="hover:text-blue-400 transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="hover:text-blue-400 transition-colors">Terms of Service</Link>
                  <Link to="/cookies" className="hover:text-blue-400 transition-colors">Cookie Policy</Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsVideoOpen(false)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-4 md:inset-10 z-50 flex flex-col bg-white rounded-3xl overflow-hidden shadow-2xl"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-xl font-semibold text-gray-900">Product Demo</h3>
                <button
                  onClick={() => setIsVideoOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              {/* Video Container */}
              <div className="flex-1 relative bg-black">
                <iframe
                  src="https://www.youtube.com/embed/YVyWpFlaKd4?autoplay=1"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, index }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="group bg-white/80 backdrop-blur-xl p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
    >
      <div className="inline-block p-3 bg-blue-100 text-blue-600 rounded-xl mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-3 text-gray-900">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </motion.div>
  );
};

export default HomePage;